import { User } from '../types/auth';
import { hasAnyPermission, hasPermission } from './permissions';

export type WorkspaceKind = 'admin' | 'logistics' | 'inspector' | 'accountable';

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  requiredPermissions: string[];
  description?: string;
  placeholder?: boolean;
}

export interface NavigationSection {
  label: string;
  items: NavigationItem[];
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export const DASHBOARD_ROUTES: Record<WorkspaceKind, string> = {
  admin: '/admin-dashboard',
  logistics: '/logistics-dashboard',
  inspector: '/inspector-dashboard',
  accountable: '/accountable-dashboard',
};

const DASHBOARD_RULES: Array<{ workspace: WorkspaceKind; requiredPermissions: string[] }> = [
  { workspace: 'admin', requiredPermissions: ['admin.dashboard.read'] },
  {
    workspace: 'logistics',
    requiredPermissions: [
      'firearm.inventory.manage',
      'firearm.are.logistics_countersign',
      'firearm.return.confirm.logistics',
    ],
  },
  { workspace: 'inspector', requiredPermissions: ['inspection.create', 'inspection.submit'] },
  {
    workspace: 'accountable',
    requiredPermissions: [
      'firearm.issuance.create',
      'firearm.are.upload',
      'firearm.are.accountable_sign',
      'firearm.return.confirm.accountable',
    ],
  },
];

const NAVIGATION_BY_WORKSPACE: Record<WorkspaceKind, NavigationSection[]> = {
  admin: [
    {
      label: 'Administration',
      items: [
        {
          id: 'admin-dashboard',
          label: 'Dashboard',
          path: DASHBOARD_ROUTES.admin,
          icon: 'LayoutDashboard',
          requiredPermissions: ['admin.dashboard.read'],
        },
        {
          id: 'admin-users',
          label: 'User Management',
          path: `${DASHBOARD_ROUTES.admin}/users`,
          icon: 'Users',
          requiredPermissions: ['admin.user.manage'],
        },
        {
          id: 'admin-departments',
          label: 'Departments',
          path: `${DASHBOARD_ROUTES.admin}/departments`,
          icon: 'Building2',
          requiredPermissions: ['admin.user.manage'],
        },
        {
          id: 'admin-roles',
          label: 'Canonical Role and Permission Management',
          path: `${DASHBOARD_ROUTES.admin}/roles`,
          icon: 'ShieldCheck',
          requiredPermissions: ['admin.role.manage'],
        },
        {
          id: 'admin-system-governance',
          label: 'System Governance',
          path: `${DASHBOARD_ROUTES.admin}/system-governance`,
          icon: 'ShieldCheck',
          requiredPermissions: ['admin.dashboard.read'],
        },
        {
          id: 'admin-data-privacy',
          label: 'Data Privacy Compliance',
          path: `${DASHBOARD_ROUTES.admin}/data-privacy`,
          icon: 'FileText',
          requiredPermissions: ['admin.dashboard.read'],
        },
        {
          id: 'admin-audit-logs',
          label: 'Audit Trail',
          path: `${DASHBOARD_ROUTES.admin}/audit-logs`,
          icon: 'FileText',
          requiredPermissions: ['admin.dashboard.read'],
        },
        {
          id: 'admin-security-center',
          label: 'Security Center',
          path: `${DASHBOARD_ROUTES.admin}/security-center`,
          icon: 'ShieldCheck',
          requiredPermissions: ['admin.dashboard.read'],
        },
      ],
    },
  ],
  inspector: [
    {
      label: 'Inspection',
      items: [
        {
          id: 'inspector-dashboard',
          label: 'Dashboard',
          path: DASHBOARD_ROUTES.inspector,
          icon: 'LayoutDashboard',
          requiredPermissions: ['dashboard.summary.read'],
        },
        {
          id: 'inspector-inspection-reports',
          label: 'Inspection Reports',
          path: `${DASHBOARD_ROUTES.inspector}/inspection-reports`,
          icon: 'FileText',
          requiredPermissions: ['inspection.create', 'inspection.submit'],
        },
        {
          id: 'inspector-reconciliation',
          label: 'Validation Results',
          path: `${DASHBOARD_ROUTES.inspector}/validation-results`,
          icon: 'GitCompareArrows',
          requiredPermissions: ['inspection.submit'],
        },
        {
          id: 'inspector-chain-of-custody',
          label: 'Chain of Custody',
          path: `${DASHBOARD_ROUTES.inspector}/chain-of-custody`,
          icon: 'History',
          requiredPermissions: ['firearm.custody.read'],
          description: 'Read-only custody records by department',
        },
        {
          id: 'inspector-notifications',
          label: 'Notifications',
          path: `${DASHBOARD_ROUTES.inspector}/notifications`,
          icon: 'Bell',
          requiredPermissions: ['notification.system.view'],
        },
        {
          id: 'inspector-inventory-lookup',
          label: 'Firearm Inventory Lookup',
          path: `${DASHBOARD_ROUTES.inspector}/firearm-inventory-lookup`,
          icon: 'PackageSearch',
          requiredPermissions: ['firearm.inventory.read'],
          description: 'Read-only firearm inventory reference',
        },
      ],
    },
  ],
  logistics: [
    {
      label: 'Logistics',
      items: [
        {
          id: 'logistics-dashboard',
          label: 'Dashboard',
          path: DASHBOARD_ROUTES.logistics,
          icon: 'LayoutDashboard',
          requiredPermissions: ['dashboard.summary.read'],
        },
        {
          id: 'logistics-inventory',
          label: 'Firearm Registry',
          path: `${DASHBOARD_ROUTES.logistics}/firearm-registry`,
          icon: 'Package',
          requiredPermissions: ['firearm.inventory.read', 'firearm.inventory.manage'],
        },
        {
          id: 'logistics-issuance-review',
          label: 'Issuance & ARE',
          path: `${DASHBOARD_ROUTES.logistics}/issuance-are`,
          icon: 'ClipboardList',
          requiredPermissions: ['firearm.issuance.read', 'firearm.are.read', 'firearm.are.logistics_countersign'],
        },
        {
          id: 'logistics-returns',
          label: 'Firearm Returns',
          path: `${DASHBOARD_ROUTES.logistics}/returns`,
          icon: 'Undo2',
          requiredPermissions: [
            'firearm.return.history.read',
            'firearm.return.revoke',
            'firearm.return.confirm.logistics',
          ],
        },
        {
          id: 'logistics-custody',
          label: 'Chain of Custody',
          path: `${DASHBOARD_ROUTES.logistics}/custody`,
          icon: 'History',
          requiredPermissions: ['firearm.custody.read'],
        },
        {
          id: 'logistics-incidents',
          label: 'Incident Reports',
          path: `${DASHBOARD_ROUTES.logistics}/incidents`,
          icon: 'AlertTriangle',
          requiredPermissions: [
            'firearm.incident.view',
            'firearm.incident.report',
            'firearm.incident.status.update',
          ],
        },
        {
          id: 'logistics-reports',
          label: 'Reports & Monitoring',
          path: `${DASHBOARD_ROUTES.logistics}/reports`,
          icon: 'FileBarChart',
          requiredPermissions: ['report.generated.view', 'report.generated.download'],
        },
        {
          id: 'logistics-notifications',
          label: 'Notifications',
          path: `${DASHBOARD_ROUTES.logistics}/notifications`,
          icon: 'BellRing',
          requiredPermissions: ['notification.system.view', 'notification.compliance.generate'],
        },
      ],
    },
  ],
  accountable: [
    {
      label: 'Accountability',
      items: [
        {
          id: 'accountable-dashboard',
          label: 'Dashboard',
          path: DASHBOARD_ROUTES.accountable,
          icon: 'LayoutDashboard',
          requiredPermissions: ['dashboard.summary.read'],
        },
        {
          id: 'accountable-assigned-firearms',
          label: 'Department Firearms',
          path: `${DASHBOARD_ROUTES.accountable}/assigned-firearms`,
          icon: 'PackageSearch',
          requiredPermissions: ['firearm.inventory.read', 'firearm.issuance.read'],
        },
        {
          id: 'accountable-officers',
          label: 'Officer Registry',
          path: `${DASHBOARD_ROUTES.accountable}/officers`,
          icon: 'Users',
          requiredPermissions: ['officer.lookup.read', 'officer.registry.manage'],
        },
        {
          id: 'accountable-issuance',
          label: 'Issuance',
          path: `${DASHBOARD_ROUTES.accountable}/issuance`,
          icon: 'ClipboardList',
          requiredPermissions: ['firearm.issuance.create', 'firearm.issuance.read'],
        },
        {
          id: 'accountable-are-upload',
          label: 'ARE Upload',
          path: `${DASHBOARD_ROUTES.accountable}/are-upload`,
          icon: 'FileUp',
          requiredPermissions: ['firearm.are.upload'],
        },
        {
          id: 'accountable-returns',
          label: 'Return Requests',
          path: `${DASHBOARD_ROUTES.accountable}/returns`,
          icon: 'Undo2',
          requiredPermissions: [
            'firearm.return.manage',
            'firearm.return.history.read',
            'firearm.return.confirm.accountable',
          ],
        },
        {
          id: 'accountable-incidents',
          label: 'Incident Reports',
          path: `${DASHBOARD_ROUTES.accountable}/incidents`,
          icon: 'Eye',
          requiredPermissions: ['firearm.incident.view'],
          description: 'Read-only incident view',
        },
        {
          id: 'accountable-notifications',
          label: 'Notifications',
          path: `${DASHBOARD_ROUTES.accountable}/notifications`,
          icon: 'Bell',
          requiredPermissions: ['notification.system.view'],
        },
      ],
    },
  ],
};

const workspacePriority = (user?: Pick<User, 'permissions'> | null): WorkspaceKind | null => {
  if (!user) return null;
  return DASHBOARD_RULES.find((rule) => hasAnyPermission(user, rule.requiredPermissions))?.workspace || null;
};

export const getWorkspaceKind = workspacePriority;

export const getDefaultDashboard = (user?: Pick<User, 'permissions'> | null): string => {
  const workspace = workspacePriority(user);
  if (workspace) return DASHBOARD_ROUTES[workspace];
  return user && hasPermission(user, 'dashboard.summary.read') ? '/dashboard' : '/login';
};

export const getRoleNavigationSections = (user?: Pick<User, 'permissions'> | null): NavigationSection[] => {
  const workspace = workspacePriority(user);
  if (!user || !workspace) return [];

  return NAVIGATION_BY_WORKSPACE[workspace]
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasAnyPermission(user, item.requiredPermissions)),
    }))
    .filter((section) => section.items.length > 0);
};

export const getRoleNavigation = (user?: Pick<User, 'permissions'> | null): NavigationItem[] =>
  getRoleNavigationSections(user).flatMap((section) => section.items);

export const getNavigationSectionsForWorkspace = (
  user: Pick<User, 'permissions'> | null | undefined,
  workspace: WorkspaceKind,
): NavigationSection[] => {
  if (!user) return [];

  return NAVIGATION_BY_WORKSPACE[workspace]
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasAnyPermission(user, item.requiredPermissions)),
    }))
    .filter((section) => section.items.length > 0);
};

export const getNavigationForWorkspace = (
  user: Pick<User, 'permissions'> | null | undefined,
  workspace: WorkspaceKind,
): NavigationItem[] =>
  getNavigationSectionsForWorkspace(user, workspace).flatMap((section) => section.items);

export const getAllowedRoutes = (user?: Pick<User, 'permissions'> | null): string[] =>
  getRoleNavigation(user).map((item) => item.path);

export const isAllowedRoute = (
  user: Pick<User, 'permissions'> | null | undefined,
  path: string
): boolean => getAllowedRoutes(user).some((route) => {
  const dashboardRoute = Object.values(DASHBOARD_ROUTES).includes(route as any);
  return dashboardRoute ? path === route : path === route || path.startsWith(`${route}/`);
});

export const getNavigationItemForPath = (
  user: Pick<User, 'permissions'> | null | undefined,
  path: string
): NavigationItem | undefined => {
  const items = getRoleNavigation(user);
  return items
    .filter((item) => path === item.path || path.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
};

export const getBreadcrumbs = (
  user: Pick<User, 'permissions'> | null | undefined,
  path: string
): BreadcrumbItem[] => {
  const dashboard = getDefaultDashboard(user);
  const item = getNavigationItemForPath(user, path);
  const dashboardItem = getRoleNavigation(user).find((navItem) => navItem.path === dashboard);
  const rootLabel = dashboardItem?.label || 'Dashboard';

  if (!item || item.path === dashboard) return [{ label: rootLabel, path: dashboard }];
  return [
    { label: rootLabel, path: dashboard },
    { label: item.label, path: item.path },
  ];
};

export const getDashboardPath = getDefaultDashboard;
